import { z } from 'zod';

/**
 * Het platformmerk en de bouwstenen van een huisstijl (MRK-1, ADR-0084).
 *
 * Twee niveaus die van boven naar beneden erven: het **platformmerk** (UP2D8 zelf, of de naam
 * waaronder een wederverkoper het product verkoopt) en daaronder de **bedrijfshuisstijl** van één
 * klant. De regel is overal dezelfde: een veld leeg laten (`null`) betekent erven. Pas als beide
 * niveaus leeg zijn, geldt de standaard van UP2D8.
 *
 * De server lost alles op en de client rekent niets uit (ADR-0056). Wat hier staat, is dus wat de
 * client krijgt en toepast - geen invoer die hij nog moet combineren.
 */

/**
 * De lettertypen waaruit gekozen kan worden.
 *
 * Een vaste lijst en geen vrij veld: de lettertypen worden zelf gehost en vooraf geladen, en elk
 * lettertype hier heeft de gewichten 400 tot 700 en de tekens van het Frans en het Nederlands (é, ë,
 * ç, œ). Een vrij veld zou een klant een lettertype laten kiezen dat op de helft van de toestellen
 * terugvalt op Arial, of een externe stylesheet die op een werkvloer zonder internet niet laadt.
 *
 * De waarde is de CSS-familienaam, precies zoals ze in `font-family` hoort.
 */
export const BRAND_FONTS = [
  'Poppins',
  'Montserrat',
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Nunito',
  'Source Sans 3',
  'Work Sans',
  'Merriweather',
] as const;
export const brandFontSchema = z.enum(BRAND_FONTS);
export type BrandFont = z.infer<typeof brandFontSchema>;

/**
 * Hoe rond de hoeken zijn.
 *
 * `rond` is de huidige set `--radius-*` uit tokens.css; `gemiddeld` en `strak` schalen die terug.
 * Drie standen en geen getal: een getal laat een klant 40 px kiezen, en dan zijn knoppen pillen en
 * kaarten ovalen - dat is een ontwerpfout die hij pas op het scherm van een ander ziet.
 */
export const brandRadiusSchema = z.enum(['rond', 'gemiddeld', 'strak']);
export type BrandRadius = z.infer<typeof brandRadiusSchema>;

/** De standaardwaarden van UP2D8, voor wie op geen enkel niveau iets koos. */
export const BRAND_DEFAULTS = {
  productName: 'UP2D8',
  fontHeading: 'Poppins',
  fontBody: 'Montserrat',
  radius: 'rond',
} as const satisfies {
  productName: string;
  fontHeading: BrandFont;
  fontBody: BrandFont;
  radius: BrandRadius;
};

/** Grenzen op de teksten; hier, zodat client en server dezelfde teller tonen. */
export const BRAND_LIMITS = {
  /** De productnaam staat in een tabtitel en naast een logo in een zijbalk van 240 px. */
  productName: 40,
  /** Een kop boven het aanmeldformulier: één regel op een telefoon. */
  loginTitle: 60,
  /** Twee à drie zinnen op het donkere vlak naast het formulier. */
  loginText: 280,
  /** Wat in het veld "Van" van een mail staat, voor het adres. */
  emailSenderName: 60,
  /** Adresgegevens van wie het product verkoopt, onderaan elke mail. */
  emailFooter: 500,
} as const;

/** Een stuurteken (onder de spatie); een regeleinde telt alleen mee als het niet mag. */
function hasControl(value: string, allowNewline: boolean): boolean {
  for (const char of value) {
    const code = char.charCodeAt(0);
    if ((code === 10 || code === 13) && allowNewline) continue;
    if (code < 32 || code === 127) return true;
  }
  return false;
}

/**
 * Een tekst die in een kop of in de afzender van een mail kan staan.
 *
 * Geen regeleinden en geen `<`, `>` of `"`: de afzendernaam komt in de header `From`, en een
 * regeleinde daar is een manier om er een eigen header achter te plakken. Voor de andere velden is
 * het dezelfde regel uit voorzorg - ze staan ook in een `<title>` en in de kop van een mail.
 */
const singleLine = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Hoogstens ${max} tekens.`)
    .refine((value) => !hasControl(value, false) && !/[<>"]/.test(value), {
      message: 'Gebruik geen regeleinde en geen <, > of ".',
    });

/** Meerdere regels mogen, maar geen andere stuurtekens en geen HTML. */
const multiLine = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Hoogstens ${max} tekens.`)
    .refine((value) => !hasControl(value, true) && !/[<>]/.test(value), {
      message: 'Gebruik geen < of >.',
    });

/**
 * Een kleur in een verzoek. De vorm wordt op de server gecontroleerd (`resolveTheme`), met een
 * Nederlandse foutmelding per veld; hier alleen een grens op de lengte.
 */
const colorInput = z.string().trim().max(16);

/**
 * De velden van een huisstijl die op **beide** niveaus bestaan, zoals ze opgeslagen zijn.
 *
 * `null` betekent: niets gekozen op dit niveau, erf van het niveau erboven.
 */
export const brandStyleSettingsSchema = z.object({
  primary: z.string().nullable(),
  accent: z.string().nullable(),
  /** `null` = de donkere tint die de CSS afleidt uit de primaire kleur. */
  sidebar: z.string().nullable(),
  fontHeading: brandFontSchema.nullable(),
  fontBody: brandFontSchema.nullable(),
  radius: brandRadiusSchema.nullable(),
  loginTitle: z.string().nullable(),
  loginText: z.string().nullable(),
  emailSenderName: z.string().nullable(),
});
export type BrandStyleSettings = z.infer<typeof brandStyleSettingsSchema>;

/**
 * Het opgeloste merk, voor alles wat een scherm tekent.
 *
 * Gedeeld door het publieke platformmerk en het bedrijfsthema. Elk veld is ingevuld door de server:
 * de waarde van het bedrijf, anders die van het platform, anders die van UP2D8.
 */
export const brandThemeFieldsSchema = z.object({
  /** De naam van het product: tabtitel, PWA-naam, aanmeldscherm. Nooit leeg; standaard "UP2D8". */
  productName: z.string(),
  /** Ondertekende URL's, zestig seconden geldig; `null` = het schild en de tekst van UP2D8. */
  productLogoUrl: z.string().url().nullable(),
  productLogoDarkUrl: z.string().url().nullable(),
  /**
   * Een vast, publiek pad (`/api/v1/platform/brand/favicon.png?v=…`) en geen ondertekende URL: een
   * browser haalt een favicon op wanneer hij wil, ook lang na zestig seconden. Relatief, net als
   * `appIconUrls`. `null` = het favicon van UP2D8 uit `public/`.
   */
  faviconUrl: z.string().nullable(),
  /**
   * De kleur van de zijbalk, al verdonkerd tot witte tekst erop minstens 4,5 : 1 haalt.
   * `null` = niemand koos er een; de CSS leidt de donkere tint af uit de primaire kleur, zoals nu.
   */
  sidebar: z.string().nullable(),
  fontHeading: brandFontSchema,
  fontBody: brandFontSchema,
  radius: brandRadiusSchema,
  /** De kop en de tekst op het donkere vlak van het aanmeldscherm; `null` = de standaardtekst. */
  loginTitle: z.string().nullable(),
  loginText: z.string().nullable(),
  /** Ondertekende URL, zestig seconden geldig; `null` = het donkere vlak zonder afbeelding. */
  loginBackgroundUrl: z.string().url().nullable(),
});
export type BrandThemeFields = z.infer<typeof brandThemeFieldsSchema>;

/** De zes kleuren die de client zet (ADR-0056), en of ze van de standaard afwijken. */
export const brandColorsSchema = z.object({
  primary: z.string(),
  primaryText: z.string(),
  onPrimary: z.string(),
  accent: z.string(),
  accentEdge: z.string(),
  onAccent: z.string(),
  /**
   * Wijken de kleuren af van die van UP2D8, door het platformmerk of door het bedrijf? Dan zet de
   * client `data-brand="custom"` en leidt de CSS de rest van het palet af.
   */
  custom: z.boolean(),
});

/**
 * Het platformmerk zoals iedereen het mag zien, ook zonder aanmelding.
 *
 * Voor het aanmeldscherm vóór de bedrijfscode: anders flitst het eerst in de stijl van UP2D8 en
 * springt het dan om. Bewust zonder de instellingen voor e-mail: wie de afzender en de voettekst
 * is, gaat een bezoeker zonder sessie niets aan.
 */
export const platformThemeSchema = brandColorsSchema.merge(brandThemeFieldsSchema);
export type PlatformTheme = z.infer<typeof platformThemeSchema>;

/** Het platformmerk zoals het opgeslagen is: `null` = de standaard van UP2D8. */
export const platformBrandSettingsSchema = brandStyleSettingsSchema.extend({
  productName: z.string().nullable(),
  emailFooter: z.string().nullable(),
});
export type PlatformBrandSettings = z.infer<typeof platformBrandSettingsSchema>;

/**
 * Wat het scherm Merk in `/platform` krijgt: de opgeslagen keuzes om het formulier te vullen, en
 * het opgeloste merk voor het voorbeeld.
 */
export const platformBrandSchema = z.object({
  settings: platformBrandSettingsSchema,
  theme: platformThemeSchema,
  /** De afzendernaam zoals een mail zonder bedrijf hem krijgt: de keuze, anders de productnaam. */
  emailSenderName: z.string(),
  emailFooter: z.string().nullable(),
  updatedAt: z.string().datetime().nullable(),
});
export type PlatformBrand = z.infer<typeof platformBrandSchema>;

/**
 * Het platformmerk wijzigen.
 *
 * Elk veld is optioneel: weggelaten = ongewijzigd, `null` = terug naar de standaard van UP2D8. Een
 * kleur geef je in paren (primair en accent samen), net als bij de bedrijfshuisstijl.
 */
export const updatePlatformBrandRequestSchema = z.object({
  productName: singleLine(BRAND_LIMITS.productName).nullable().optional(),
  primary: colorInput.nullable().optional(),
  accent: colorInput.nullable().optional(),
  sidebar: colorInput.nullable().optional(),
  fontHeading: brandFontSchema.nullable().optional(),
  fontBody: brandFontSchema.nullable().optional(),
  radius: brandRadiusSchema.nullable().optional(),
  loginTitle: singleLine(BRAND_LIMITS.loginTitle).nullable().optional(),
  loginText: multiLine(BRAND_LIMITS.loginText).nullable().optional(),
  emailSenderName: singleLine(BRAND_LIMITS.emailSenderName).nullable().optional(),
  emailFooter: multiLine(BRAND_LIMITS.emailFooter).nullable().optional(),
});
export type UpdatePlatformBrandRequest = z.infer<typeof updatePlatformBrandRequestSchema>;

/** De velden die een bedrijf boven op zijn kleuren en naam kan zetten (zie de tenants-index). */
export const brandStyleRequestFields = {
  sidebar: colorInput.nullable().optional(),
  fontHeading: brandFontSchema.nullable().optional(),
  fontBody: brandFontSchema.nullable().optional(),
  radius: brandRadiusSchema.nullable().optional(),
  loginTitle: singleLine(BRAND_LIMITS.loginTitle).nullable().optional(),
  loginText: multiLine(BRAND_LIMITS.loginText).nullable().optional(),
  emailSenderName: singleLine(BRAND_LIMITS.emailSenderName).nullable().optional(),
};
